import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Select, Space, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

/** Personal shortcuts backed by the same account-scoped groups as Bank Balance Summary. */
export default function OwnerGroupPicker({ value, onSelect, ownerOptions, loadOwners }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const selected = groups.find((group) => group.id === value);
  const accept = (response) => {
    if (String(response?.code) !== "1") throw new Error(response?.msg || "Unable to update owner groups.");
    return response.data;
  };
  const refresh = async () => {
    setLoading(true);
    try {
      const next = accept(await http.get(api.getBalanShowGroupList)) || [];
      setGroups(next);
      // Reflect edits made in the other report when this shortcut is refreshed.
      if (value != null) onSelect(next.find((group) => group.id === value) || null);
      return next;
    } catch (error) { message.error(error.message || "Unable to load owner groups."); return null; }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let active = true;
    setLoading(true);
    http.get(api.getBalanShowGroupList).then((response) => {
      const next = accept(response) || [];
      if (active) setGroups(next);
    }).catch((error) => { if (active) message.error(error.message || "Unable to load owner groups."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  const openEditor = (group) => {
    loadOwners?.();
    const next = group || { groupName: "", ownerIds: [] };
    setEditing(next);
    form.setFieldsValue({ groupName: next.groupName, ownerIds: next.ownerIds || [] });
  };
  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      accept(await http.post(editing.id ? api.getUpdateBalanGroupList : api.getAddGroupList, {
        ...(editing.id ? { id: editing.id } : {}), groupName: values.groupName.trim(), ownerIds: values.ownerIds,
      }));
      setEditing(null);
      const next = await refresh();
      const saved = next?.find((group) => editing.id ? group.id === editing.id : group.groupName === values.groupName.trim());
      if (saved) onSelect(saved);
      message.success("Owner group saved across your reports.");
    } catch (error) { if (!error.errorFields) message.error(error.message || "Unable to save owner group."); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    setSaving(true);
    try {
      accept(await http.post(api.getDeleteBalanGroupList, { id: selected.id }));
      onSelect(null);
      await refresh();
      message.success("Owner group deleted across your reports.");
    } catch (error) { message.error(error.message || "Unable to delete owner group."); }
    finally { setSaving(false); }
  };
  // Keep members whose dictionary entries are unavailable visible and editable by ID.
  const options = [...ownerOptions];
  for (const id of editing?.ownerIds || []) {
    if (!options.some((option) => option.value === id)) options.push({ value: id, label: `Owner #${id}` });
  }
  return <div className="one-line-owner-shortcuts">
    <div className="one-line-owner-shortcuts-title"><span><TeamOutlined /> My owner groups</span><Button type="text" size="small" aria-label="Refresh owner groups" icon={<ReloadOutlined />} loading={loading} onClick={refresh} /></div>
    <Select aria-label="Owner group shortcut" showSearch allowClear optionFilterProp="label" placeholder="Choose an owner group" value={value ?? undefined} loading={loading} disabled={saving}
      onDropdownVisibleChange={(open) => { if (open) refresh(); }}
      options={groups.map((group) => ({ value: group.id, label: `${group.groupName} (${group.ownerIds?.length || 0})` }))}
      onChange={(id) => onSelect(groups.find((group) => group.id === id) || null)} />
    <div className="one-line-owner-shortcuts-bottom"><small>Shared across your reports</small><Space size={0}>
      <Button type="text" size="small" aria-label="Create owner group" icon={<PlusOutlined />} disabled={saving} onClick={() => openEditor(null)} />
      <Button type="text" size="small" aria-label="Edit owner group" icon={<EditOutlined />} disabled={!selected || saving} onClick={() => openEditor(selected)} />
      <Popconfirm title="Delete this personal owner group?" description="This removes the shortcut from Bank Balance Summary, Operating Statement Analysis, One Line Cash Flow and General Ledger." onConfirm={remove} okText="Delete" okButtonProps={{ danger: true }}>
        <Button type="text" size="small" danger aria-label="Delete owner group" icon={<DeleteOutlined />} disabled={!selected || saving} />
      </Popconfirm>
    </Space></div>
    <Modal maskClosable={false} title={editing?.id ? "Edit owner group" : "New owner group"} open={!!editing} forceRender width={540} onCancel={() => setEditing(null)} onOk={save} confirmLoading={saving} okText="Save and select">
      <p className="one-line-owner-modal-note">Personal to your account. Shared by Bank Balance Summary, Operating Statement Analysis, One Line Cash Flow and General Ledger.</p>
      <Form form={form} layout="vertical">
        <Form.Item name="groupName" label="Group name" rules={[{ required: true, whitespace: true, max: 255 }]}><Input maxLength={255} placeholder="e.g. My portfolio" /></Form.Item>
        <Form.Item name="ownerIds" label="Owners" rules={[{ required: true, type: "array", min: 1, message: "Select at least one owner" }]}><Select mode="multiple" showSearch optionFilterProp="label" options={options} placeholder="Search owners" /></Form.Item>
      </Form>
    </Modal>
  </div>;
}
